import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      // Only apply scroll lock if body doesn't already have the class (no other dialogs are open)
      if (!document.body.classList.contains('dialog-scroll-lock')) {
        document.body.classList.add('dialog-scroll-lock');
        document.body.style.top = `-${scrollY}px`;
      }

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Checks if there are any other open dialogs (modals or sub-modals)
   * @param {boolean} excludeCurrent - Whether to exclude the current dialog from the count
   * @returns {boolean} True if there are other open dialogs
   */
  #hasOtherOpenDialogs(excludeCurrent = false) {
    const allDialogs = document.querySelectorAll('dialog-component dialog[open]');
    const count = excludeCurrent ? allDialogs.length - 1 : allDialogs.length;
    return count > 0;
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    const isSubModal = this.classList.contains('sub-modal');

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // For sub-modals, immediately restore parent modal state, then animate close
    if (isSubModal) {
      // Immediately remove the scale/opacity from parent modal
      const parentModal = document.querySelector('.header-menu-modal__dialog[open]');
      if (parentModal) {
        parentModal.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
        parentModal.style.transform = 'scale(1)';
        parentModal.style.opacity = '1';
        parentModal.style.pointerEvents = '';
      }

      // Start closing animation
      dialog.style.animation = 'none';
      void dialog.offsetWidth;
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      // Close dialog after short delay (don't wait for full animation)
      setTimeout(() => {
        dialog.close();
        dialog.classList.remove('dialog-closing');
        this.dispatchEvent(new DialogCloseEvent());
        
        // Remove inline styles after transition completes so CSS rule can work again on next open
        setTimeout(() => {
          if (parentModal) {
            parentModal.style.transition = '';
            parentModal.style.transform = '';
            parentModal.style.opacity = '';
            parentModal.style.pointerEvents = '';
          }
        }, 300); // After transition completes
      }, 150); // Half of animation duration for smoother feel
      return;
    }

    // For regular modals, wait for animation
    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    // Only remove scroll lock if no other dialogs are open (excluding the one we just closed)
    if (!this.#hasOtherOpenDialogs(true)) {
      document.body.classList.remove('dialog-scroll-lock');
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    // Clear URL hash only if this is not a sub-modal
    if (!isSubModal && window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;
    const isSubModal = this.classList.contains('sub-modal');

    // For sub-modals, also check if click is on the parent modal (backdrop area)
    if (isSubModal) {
      const parentModal = document.querySelector('dialog-component.header-menu-modal dialog[open]');
      // If click is outside sub-modal dialog OR on the parent modal backdrop, close sub-modal
      if (isClickedOutside(event, dialog) || (parentModal && parentModal.contains(event.target) && event.target !== dialog && !dialog.contains(event.target))) {
        this.closeDialog();
        return;
      }
    }

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
